import {fetchGitHubHostedRunners, fetchSelfHostedRunners} from '@github-ui/actions-runners/fetch-runners'
import type {GitHubHostedRunnersResponse, SelfHostedRunnersData} from '@github-ui/actions-runners/types'
import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import {QueryRouteQueryType} from '@github-ui/react-core/future/data-router-types'
import {repoAppBuilder} from '@github-ui/repo-app-builder'
import {IS_BROWSER} from '@github-ui/ssr-utils'

export type ActionsRunnersTab = 'github-hosted' | 'self-hosted'

export type ActionsRunnersQueryData =
  | {tab: 'github-hosted'; runners: GitHubHostedRunnersResponse}
  | {tab: 'self-hosted'; runners: SelfHostedRunnersData}

export function selectedRunnersTab(searchParams: URLSearchParams): ActionsRunnersTab {
  return searchParams.get('tab') === 'self-hosted' ? 'self-hosted' : 'github-hosted'
}

/**
 * Starts the selected tab's runner requests while the route loads, in parallel with the
 * layout request, instead of after the component mounts. The query is deferred so it never
 * blocks navigation, and the page keeps showing its loading state until the data arrives.
 */
const runnersQuery = (_routeId: string) => ({
  // The workflows layout reads its child route's data through this name.
  queryName: 'mainQuery' as const,
  queryDeps: ({pathname, searchParams}: {pathname: string; searchParams: URLSearchParams}) => ({
    pathname,
    tab: selectedRunnersTab(searchParams),
  }),
  queryFn: async (
    {queryDeps: {pathname, tab}}: {queryDeps: {pathname: string; tab: ActionsRunnersTab}},
    {signal}: {signal: AbortSignal},
  ): Promise<ActionsRunnersQueryData> =>
    tab === 'self-hosted'
      ? {tab, runners: await fetchSelfHostedRunners(pathname, signal)}
      : {tab, runners: await fetchGitHubHostedRunners(pathname, signal)},
  type: QueryRouteQueryType.Deferred,
  // Long enough to share the loader's request with the mounted component, short enough that
  // returning to the page shows current runner status.
  staleTime: 10_000,
  retryOnMount: false,
  // Runners are fetched in the browser only; the server renders the loading state.
  enabled: () => IS_BROWSER,
})

export function buildActionsRunnersRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  return appBuilder.createQueryRouteConfig('actionsRunnersRoute', {
    path: '/:owner/:repo/actions/runners',
    queries: [runnersQuery],
  })
}

export const actionsRunnersRoute = buildActionsRunnersRoute(repoAppBuilder)
